import { HARDENING_CLUSTERS, HARDENING_POLICY_STATUS } from '@/lib/constants/hardening'
import { MDM_CHECK_NOT_CHECKED } from '@/lib/constants/mdm/policies'

export const MDM_DASHBOARD = {
  TITLE: 'MDM Dashboard',
  SUMMARY: (enrolled: number, online: number, offline: number) =>
    `${enrolled} ${enrolled === 1 ? 'host' : 'hosts'} enrolled · ${online} online · ${offline} offline`,
  SHORTCUTS: {
    NEW_GROUP: 'New group',
    NEW_POLICY: 'New policy',
    ADD_SOFTWARE: 'Add software',
    RUN_SCRIPT: 'Run script',
  },
  COVERAGE: {
    TITLE: 'MDM coverage',
    SUBTITLE: 'IT Inventory devices enrolled in MDM',
    ENROLLED: 'enrolled',
    LAPTOPS: 'Laptops',
    PHONES: 'Phones',
    PENDING: (count: number) => `${count} pending enrollment`,
  },
  BASELINE: {
    TITLE: 'Security baseline',
    SUBTITLE: (compliant: number, total: number) =>
      `${compliant} of ${total} ${total === 1 ? 'host passes' : 'hosts pass'} every check`,
    COMPLIANT: 'compliant',
    VIEW_HOSTS: 'View hosts',
    FAILED: (count: number) => `${count} failed`,
    ALL_PASS: 'All pass',
    WARNING: (count: number) => `${count} warning`,
    SEPARATOR: ' · ',
  },
  NON_COMPLIANT_HOSTS: {
    TITLE: 'Non-compliant hosts',
    OPEN_HOSTS: 'Open Hosts',
    EMPTY: 'All hosts pass the baseline',
  },
  MORE_ROWS: (count: number) => `+${count} more`,
  VIEW_ALL: 'View all',
  NOT_ENROLLED: {
    TITLE: 'Not enrolled',
    OPEN_IT_INVENTORY: 'Open IT Inventory',
    PENDING: 'Pending',
    NO_AGENT: 'MDM agent not installed',
    EMPTY: 'All devices are enrolled',
  },
  HOSTS_BREAKDOWN: {
    TITLE: 'Hosts',
    ENROLLED: (count: number) => `${count} ${count === 1 ? 'host' : 'hosts'} enrolled`,
    ONLINE_SECTION: 'Online',
    OFFLINE_SECTION: 'Offline',
    STATUS_NOT_SUPPORTED: 'Not supported',
    COLUMN_HOST: 'Host',
    COLUMN_GROUPS: 'Groups',
    COLUMN_ASSIGNED_TO: 'Assigned to',
    COLUMN_LAST_CHECK_IN: 'Last check-in',
    UNASSIGNED: 'Unassigned',
    COMPLIANCE_PASS: 'Passed',
    COMPLIANCE_FAIL: 'Failed',
    COMPLIANCE_PENDING: 'Pending',
    COMPLIANCE_ACTION_REQUIRED: 'Action required',
    COMPLIANCE_ACTION_REQUIRED_INFO:
      'Waiting for the user: they must log out and log back in, or restart, so the device can finish disk encryption with their password and save the recovery key.',
    COMPLIANCE_NOT_APPLICABLE: '-',
    NO_POLICIES: 'No policies',
    FILTER_BY_GROUP: 'Filter by group',
    DEVICE_TYPE_ALL: 'All',
    DEVICE_TYPE_LAPTOPS: 'Laptops',
    DEVICE_TYPE_MOBILES: 'Phones',
    SEARCH_PLACEHOLDER: 'Search hosts or groups...',
    DRAWER: {
      SECTION_COMPLIANCE: 'Compliance',
      SECTION_MANAGEMENT: 'Management',
      SECTION_POLICIES: (count: number) => `Device policies (${count})`,
      SECTION_APPLICATIONS: 'Applications',
      SECTION_MORE_DETAILS: 'More details',
      SECTION_ACTIONS: 'Actions',
      POSTURE: 'Posture',
      PHONE_ENCRYPTION: 'Built in',
      PHONE_ENCRYPTION_INFO:
        'Phones encrypt their storage by design: iPhone turns on Data Protection once a passcode is set, and Android 10 and later always use file-based encryption. Enforcing the passcode is what protects it.',
      NOT_APPLICABLE_INFO: {
        [HARDENING_CLUSTERS.PRIVILEGE_PROTECTION]:
          'Phones have no admin accounts to restrict: every app runs sandboxed, without admin rights.',
        [HARDENING_CLUSTERS.HOST_FIREWALL]:
          'Phones have no firewall to set up: apps are sandboxed, so they cannot reach each other or the system.',
      } as Record<string, string>,
      POLICIES_APPLIED: (applied: number, total: number) => `${applied} of ${total} applied`,
      IT_INVENTORY_LINK: 'View in IT Inventory',
      IT_INVENTORY_LINK_DETAIL: 'Hardware and storage details',
      DISK_FREE: (free: string, total: string) => `${free} free of ${total}`,
      DISK_TOTAL: (total: string) => `${total} total`,
      DISK_UNKNOWN: 'Not reported by the device',
      APPLICATIONS_COUNT: (count: number) => `${count} installed`,
      APPLICATIONS_EMPTY: 'No applications reported by the device.',
      APPLICATIONS_SEARCH_PLACEHOLDER: 'Search applications',
      APPLICATIONS_NO_RESULTS: 'No applications match your search.',
      APPLICATIONS_ERROR: 'Could not load the applications for this device.',
      APPLICATIONS_HINT_ANDROID:
        'Only apps in the work profile are listed. Apps on the personal side of the phone are not visible to MDM.',
      NOTIFY_USER: 'Notify the user to take action',
      NOTIFY_USER_DIALOG_TITLE: 'Notify the user',
      NOTIFY_USER_DIALOG_DESCRIPTION: (hostname: string) =>
        `A message will appear on ${hostname} asking the user to log out and log back in, or restart. MacOS needs their password once to finish disk encryption.`,
      NOTIFY_USER_DIALOG_PREVIEW_LABEL: 'The user will see',
      NOTIFY_USER_CANCEL: 'Cancel',
      NOTIFY_USER_CONFIRM: 'Notify the user',
      NOTIFY_USER_SENDING: 'Sending...',
      NOTIFY_USER_SENT: 'Message sent to the user',
      NOTIFY_USER_FAILED: 'Could not notify the user',
      DISK_ENCRYPTION_NOTICE_TITLE: 'Disk encryption',
      DISK_ENCRYPTION_NOTICE_MESSAGE:
        'Your organization is turning on disk encryption on this Mac. To finish, log out and log back in, or restart. You only need to do this once.',
    },
  },
  CHECKS: {
    [HARDENING_CLUSTERS.PASSWORD_SECURITY]: 'Password policy',
    [HARDENING_CLUSTERS.DISK_ENCRYPTION]: 'Disk encryption',
    [HARDENING_CLUSTERS.SCREEN_LOCK]: 'Screen lock',
    [HARDENING_CLUSTERS.PRIVILEGE_PROTECTION]: 'Privilege protection',
    [HARDENING_CLUSTERS.HOST_FIREWALL]: 'Firewall',
  },
  CHECK_STATES: {
    [HARDENING_POLICY_STATUS.PASSED]: 'Passed',
    [HARDENING_POLICY_STATUS.WARNING]: 'Warning',
    [HARDENING_POLICY_STATUS.FAILED]: 'Failed',
    [HARDENING_POLICY_STATUS.NOT_APPLICABLE]: 'Not applicable',
    [MDM_CHECK_NOT_CHECKED]: 'Not checked',
  },
  POLICY_POSTURE: {
    COMPLIANT: 'Compliant',
    NON_COMPLIANT: 'Non-compliant',
  },
}
